import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  ACTION_OWNER_ROLE_HELPER,
  ownerRoleIsUnassigned,
} from '../../domain/incidents/incidentPatternVocabulary';
import { FOLLOWUP_OWNER_FIELD_HELPER } from '../../domain/incidents/groupedRepresentativeAuditCopy';
import { GROUPED_ASSIGN_ACTION_STEP_TITLES } from '../../domain/incidents/groupedAssignActionWorkflow';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';

export type GroupedAssignActionPanelProps = {
  followUpOwnerLabel?: string;
  actionOwnerDraft?: string;
  onActionOwnerDraftChange?: (value: string) => void;
  onSaveActionOwner?: (owner: string) => void | Promise<void>;
  canSaveActionOwner?: boolean;
  actionOwnerSaveBusy?: boolean;
  ownerSaveFeedback?: { severity: 'success' | 'info' | 'warning'; message: string } | null;
  panelRef?: React.Ref<HTMLDivElement>;
};

export const GroupedAssignActionPanel: React.FC<GroupedAssignActionPanelProps> = ({
  followUpOwnerLabel,
  actionOwnerDraft = '',
  onActionOwnerDraftChange,
  onSaveActionOwner,
  canSaveActionOwner = false,
  actionOwnerSaveBusy = false,
  ownerSaveFeedback = null,
  panelRef,
}) => {
  const actionOwnerUnassigned = ownerRoleIsUnassigned(followUpOwnerLabel);
  const fieldKey = `${String(followUpOwnerLabel || '').trim()}:${actionOwnerDraft}`;
  const [draft, setDraft] = useState(actionOwnerDraft);
  const hydrationRef = useRef('');

  useEffect(() => {
    if (hydrationRef.current === fieldKey) return;
    hydrationRef.current = fieldKey;
    setDraft(
      actionOwnerUnassigned
        ? String(actionOwnerDraft || '')
        : String(followUpOwnerLabel || actionOwnerDraft || ''),
    );
  }, [actionOwnerDraft, actionOwnerUnassigned, fieldKey, followUpOwnerLabel]);

  const pendingDraft = String(draft || '').trim();

  return (
    <Paper
      ref={panelRef}
      variant="outlined"
      data-testid="grouped-assign-action-panel"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 3,
        borderColor: '#d0d7e2',
        bgcolor: '#fbfcfe',
      }}
    >
      <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
        {GROUPED_ASSIGN_ACTION_STEP_TITLES.owner}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.25, lineHeight: 1.55 }}>
        {ACTION_OWNER_ROLE_HELPER} Assign here for this review item; this does not change the risk owner.
      </Typography>
      {ownerSaveFeedback ? (
        <Alert
          severity={ownerSaveFeedback.severity}
          sx={{ mb: 1.25, borderRadius: 2 }}
          data-testid="grouped-assign-action-owner-saved"
        >
          {ownerSaveFeedback.message}
        </Alert>
      ) : null}
      {actionOwnerUnassigned ? null : (
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.25 }} data-testid="grouped-assign-action-owner-current">
          {displaySafetyManagerLabel('followup_owner')}: {followUpOwnerLabel}
        </Typography>
      )}
      <Grid container spacing={1} alignItems="flex-start" data-testid="grouped-assign-action-owner-form">
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            size="small"
            label={displaySafetyManagerLabel('followup_owner')}
            type="text"
            autoComplete="off"
            value={draft}
            disabled={!canSaveActionOwner || actionOwnerSaveBusy}
            onChange={(event) => {
              const next = event.target.value;
              setDraft(next);
              onActionOwnerDraftChange?.(next);
            }}
            placeholder="e.g. ops-lead@site.com"
            helperText={FOLLOWUP_OWNER_FIELD_HELPER}
            inputProps={{ autoComplete: 'off' }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            disabled={!canSaveActionOwner || actionOwnerSaveBusy || !pendingDraft}
            onClick={() => {
              onActionOwnerDraftChange?.(pendingDraft);
              void onSaveActionOwner?.(pendingDraft);
            }}
            data-testid="grouped-save-action-owner"
            sx={{ fontWeight: 800, mt: { xs: 0, sm: 0.25 } }}
          >
            {actionOwnerSaveBusy ? 'Saving…' : 'Save action owner'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default GroupedAssignActionPanel;
